import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Subscription, filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, insuranceOperationsTopic, liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsurancePolicyListItem } from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-shell',
  imports: [AinAmountPipe, AinDatePipe, AinPageComponent, AinStateComponent, InfoHintComponent, TableModule, ButtonModule, InputTextModule],
  template: `
    <ain-page
      [title]="t('insurance.policies.title')"
      [description]="t('insurance.policies.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.policies.help.page.title'"
        [descriptionKey]="'insurance.policies.help.page.description'"
        [bulletKeys]="[
          'insurance.policies.help.page.bullets.scope',
          'insurance.policies.help.page.bullets.lifecycle',
          'insurance.policies.help.page.bullets.live'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.actions.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.policies.help.listFilters.title'"
                [descriptionKey]="'insurance.policies.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.policies.help.listFilters.bullets.search',
                  'insurance.policies.help.listFilters.bullets.sort',
                  'insurance.policies.help.listFilters.bullets.selection'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.states.emptyPolicies')" [message]="t('insurance.states.emptyPoliciesMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>
                    <button type="button" class="sort-btn" (click)="sortBy('policyNumber')">{{ t('insurance.policies.fields.policyNumber') }}</button>
                  </th>
                  <th>{{ t('insurance.policies.fields.product') }}</th>
                  <th>
                    <button type="button" class="sort-btn" (click)="sortBy('lineOfBusiness')">{{ t('insurance.policies.fields.lineOfBusiness') }}</button>
                  </th>
                  <th>
                    <button type="button" class="sort-btn" (click)="sortBy('premiumAmount')">{{ t('insurance.policies.fields.premium') }}</button>
                  </th>
                  <th>{{ t('insurance.policies.fields.period') }}</th>
                  <th>
                    <button type="button" class="sort-btn" (click)="sortBy('status')">{{ t('columns.status') }}</button>
                  </th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.policyNumber }}</button></td>
                  <td>{{ row.productName }}</td>
                  <td>{{ row.lineOfBusiness }}</td>
                  <td>{{ row.premiumAmount | ainAmount }} {{ row.currencyCode }}</td>
                  <td>{{ row.effectiveFromUtc | ainDate }} - {{ row.effectiveToUtc | ainDate }}</td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card{border:1px solid #e5e7eb;border-radius:12px;background:#fff;padding:12px}
    .panel{display:grid;gap:10px}
    .toolbar{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
    .filters{border:1px solid #e5e7eb;border-radius:10px;padding:8px 10px;background:#f9fafb}
    .filters summary{display:flex;align-items:center;gap:8px}
    .filter-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-top:8px}
    .filter-grid label{display:grid;gap:6px}
    .link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}
    .sort-btn{border:0;background:none;color:inherit;font:inherit;cursor:pointer;padding:0}
    .live-status{margin-inline-start:auto;font-size:12px;color:#4b5563}
    .info{color:#1d4ed8}
    .error{color:#b91c1c}
  `]
})
export class InsuranceShellComponent implements OnInit, OnDestroy {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveTopic: string | null = null;
  private liveEventsSubscription: Subscription | null = null;
  readonly loading = signal(true);
  readonly error = signal('');
  readonly info = signal('');
  readonly rows = signal<InsurancePolicyListItem[]>([]);
  readonly selectedRows = signal<InsurancePolicyListItem[]>([]);
  readonly search = signal('');
  readonly sortField = signal<'policyNumber' | 'lineOfBusiness' | 'premiumAmount' | 'status'>('policyNumber');
  readonly sortOrder = signal<1 | -1>(1);

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    this.liveEventsSubscription?.unsubscribe();
    if (this.liveTopic) {
      void this.liveUpdateService.unsubscribe(this.liveTopic);
      this.liveTopic = null;
    }
  }

  load(): void {
    this.loading.set(true);
    this.api.listPolicies().subscribe({
      next: rows => {
        this.rows.set(rows);
        this.error.set('');
        this.info.set('');
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.error.set(this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsurancePolicyListItem[] {
    const value = this.search().trim().toLowerCase();
    const filtered = !value
      ? this.rows()
      : this.rows().filter(row =>
      row.policyNumber.toLowerCase().includes(value)
      || row.productName.toLowerCase().includes(value)
      || row.lineOfBusiness.toLowerCase().includes(value));

    return [...filtered].sort((left, right) => {
      const field = this.sortField();
      const order = this.sortOrder();
      const leftValue = left[field];
      const rightValue = right[field];
      if (leftValue === rightValue) {
        return 0;
      }
      return (leftValue! > rightValue! ? 1 : -1) * order;
    });
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onSelectionChange(selection: InsurancePolicyListItem[] | InsurancePolicyListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/policies', id], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  openCreate(): void {
    void this.router.navigate(['/insurance/policies/new']);
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('insurance.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('insurance.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('insurance.actions.exportEmpty'));
      return;
    }

    const header = ['PolicyNumber', 'ProductName', 'LineOfBusiness', 'Status', 'PremiumAmount', 'CurrencyCode'];
    const lines = rows.map(item =>
      [item.policyNumber, item.productName, item.lineOfBusiness, item.status, item.premiumAmount.toString(), item.currencyCode]
        .map(value => `"${value.replace(/"/g, '""')}"`)
        .join(','));
    const csv = [header.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'insurance-policies.csv';
    anchor.click();
    URL.revokeObjectURL(url);
    this.info.set(this.t('insurance.actions.exportReady'));
  }

  sortBy(field: 'policyNumber' | 'lineOfBusiness' | 'premiumAmount' | 'status'): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() === 1 ? -1 : 1);
      return;
    }

    this.sortField.set(field);
    this.sortOrder.set(1);
  }

  liveStatusTranslationKey(state: 'disconnected' | 'connecting' | 'connected' | 'reconnecting'): string {
    return liveStatusTranslationKey(state);
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const topic = insuranceOperationsTopic(tenantId);
    this.liveTopic = topic;
    await this.liveUpdateService.subscribe(topic);
    this.liveEventsSubscription = this.liveUpdateService.events$
      .pipe(filter(event => event.eventType === LiveUpdateEventTypes.insurancePoliciesUpdated))
      .subscribe(() => this.load());
  }

  statusKey(status: InsurancePolicyListItem['status']): string {
    return `insurance.policies.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
